import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Button, Form, InputGroup } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FaSearch, FaFire, FaClock, FaPlus } from 'react-icons/fa';
import './Community.css';
import CategoryCard from './shared/CategoryCard';
import ThreadCard from './shared/ThreadCard';
import { useAuth } from '../../../contexts/AuthContext';

export default function CommunityHome() {
  const [categories, setCategories] = useState([]);
  const [recentThreads, setRecentThreads] = useState([]);
  const [trendingThreads, setTrendingThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        setLoading(true);
        // In a real app we'd use axios or fetch with the API URL
        const headers = currentUser ? {
          'Authorization': `Bearer ${localStorage.getItem('bbr.cachedUserDetails') ? JSON.parse(localStorage.getItem('bbr.cachedUserDetails')).token : ''}`
        } : {};

        // Fetch categories
        const catRes = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/categories`, { headers });
        const catData = await catRes.json();
        if (catData.success) setCategories(catData.categories);

        // Fetch recent threads
        const recRes = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/discussions?sort=recent&limit=5`, { headers });
        const recData = await recRes.json();
        if (recData.success) setRecentThreads(recData.discussions);

        // Fetch trending threads
        const trendRes = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/discussions?sort=popular&limit=5`, { headers });
        const trendData = await trendRes.json();
        if (trendData.success) setTrendingThreads(trendData.discussions);

      } catch (error) {
        console.error("Error fetching community home data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHomeData();
  }, [currentUser]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      navigate(`/community/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  if (loading) {
    return (
      <div className="community-container d-flex justify-content-center align-items-center">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  return (
    <div className="community-container">
      <Container>
        {/* Header Area */}
        <Row className="mb-5 align-items-center">
          <Col lg={8}>
            <h1 className="display-4 fw-bold text-white mb-3">BBR Community</h1>
            <p className="lead text-muted">
              Connect with other BBR members.
            </p>
          </Col>
          <Col lg={4} className="text-lg-end mt-4 mt-lg-0">
            <Link to={currentUser ? "/community/new" : "/auth"} className="btn btn-warning btn-lg fw-bold">
              <FaPlus className="me-2" /> Start Discussion
            </Link>
          </Col>
        </Row>

        {/* Search Bar */}
        <Row className="mb-5">
          <Col lg={8} className="mx-auto">
            <Form onSubmit={handleSearch}>
              <InputGroup size="lg" className="shadow">
                <Form.Control
                  placeholder="Search discussions, lakes, or guides..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-dark text-white border-secondary"
                  style={{ borderRadius: '8px 0 0 8px' }}
                />
                <Button variant="warning" type="submit" style={{ borderRadius: '0 8px 8px 0' }}>
                  <FaSearch /> Search
                </Button>
              </InputGroup>
            </Form>
          </Col>
        </Row>

        {/* Categories Grid */}
        <Row className="mb-5">
          <Col>
            <div className="d-flex justify-content-between align-items-end mb-4 border-bottom border-dark pb-2">
              <h2 className="h3 mb-0 text-white">Channels</h2>
            </div>
            <div className="category-grid">
              {categories.map(category => (
                <CategoryCard key={category.id} category={category} />
              ))}
            </div>
          </Col>
        </Row>

        {/* Feeds */}
        <Row>
          {/* Recent */}
          <Col lg={6} className="mb-5">
            <div className="d-flex justify-content-between align-items-end mb-4 border-bottom border-dark pb-2">
              <h2 className="h4 mb-0 text-white"><FaClock className="me-2 text-warning" /> Recent Activity</h2>
            </div>
            <div>
              {recentThreads.length > 0 ? (
                recentThreads.map(thread => (
                  <ThreadCard key={thread.id} thread={thread} />
                ))
              ) : (
                <p className="text-muted">No recent discussions found.</p>
              )}
            </div>
          </Col>

          {/* Trending */}
          <Col lg={6} className="mb-5">
            <div className="d-flex justify-content-between align-items-end mb-4 border-bottom border-dark pb-2">
              <h2 className="h4 mb-0 text-white"><FaFire className="me-2 text-danger" /> Trending Now</h2>
            </div>
            <div>
              {trendingThreads.length > 0 ? (
                trendingThreads.map(thread => (
                  <ThreadCard key={thread.id} thread={thread} />
                ))
              ) : (
                <p className="text-muted">No trending discussions found.</p>
              )}
            </div>
          </Col>
        </Row>

      </Container>
    </div>
  );
}
